import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {ReportReferenceReported, ReportReferencesDataService} from 'src/features/erp-sutor/features/report-references-page';
import type {FinishedResponseInteractionService, WalkerService} from 'shared/services';
import {FilterableDatatype, addFilterQtToSearchParams, mapFieldTypeIdToFilterField} from 'src/features/search/kp-search-field-type-filter';
import type {Department, DepartmentResponse, Rec, RecordHolding, RecordSearchParams} from 'src/typings';
import type {RecordGridDataService} from 'src/features/record-grid/services/record-grid.data-service';
import type {RecordBulkEditService} from 'src/features/record/record-bulk-edit.service';
import type RecordService from 'src/features/record/record.service';
import type RecordDataService from 'src/features/record/record.data-service';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {loc} from 'shared/utils/pipes';
import {pipe} from 'core/utils';
import {SUTOR_REPORT_STATES} from '../../sutor-constants';
import {FOND_REFERENCE, FOND_REPORT} from '../../sutor-fonds';

export class ReportProcessingService {
    public static serviceName = 'reportProcessingService';

    /*@ngInject*/
    constructor(public readonly walker: WalkerService,
                public readonly currentDepartment: Department,
                private recordDataService: RecordDataService,
                private toastMessageService: ToastMessageService,
                private modalDialogService: ModalDialogService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private recordGridDataService: RecordGridDataService,
                private recordBulkEditService: RecordBulkEditService,
                private reportReferencesDataService: ReportReferencesDataService,
                private recordService: RecordService) {
    }

    public async getTotalReported(reference: Rec): Promise<ReportReferenceReported> {
        try {
            return await this.reportReferencesDataService.getTotalReportedValues(reference.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async getRefreshedReference(reference: Rec): Promise<Rec | null> {
        try {
            return await this.recordDataService.getById(reference.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async setTotalHoursOnReference(reference: Rec, totalHours: number): Promise<void> {
        try {
            if (totalHours < 0) {
                this.toastMessageService.showError('Celkový počet hodin nemůže být záporný!');
                return;
            }

            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(FOND_REFERENCE.totalHoursFieldId, [0, 0]),
                        setFieldValueRequest: {value: totalHours},
                        additionalInvalidatingRecords: []
                    }
                ]
            });

            this.toastMessageService.showSuccess(`Celkový počet hodin byl úspěšně nastaven na ${totalHours}!`);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async setReferenceDate(reference: Rec, referenceDate: string): Promise<void> {
        try {
            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(FOND_REFERENCE.dateFieldId, [0, 0]),
                        setFieldValueRequest: {value: referenceDate},
                        additionalInvalidatingRecords: []
                    }
                ]
            });

            const formattedDate = pipe(referenceDate, (date) => new Date(date), (date) => date.toLocaleDateString());

            this.toastMessageService.showSuccess(`Datum na průvodce byl úšpěšně nastaven na ${formattedDate}!`);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async markReferenceAsInProgress(referenceRecord: Rec, searchParams: Partial<RecordSearchParams>): Promise<void> {
        try {
            await this.modalDialogService.openModalWindow('confirmationDialog', {text: 'Nastavit výkazy stavem "Rozpracováno"?'});
            await this.markReportsWithState(referenceRecord, searchParams, SUTOR_REPORT_STATES.IN_PROGRESS);
            this.toastMessageService.showSuccess('Stav úspěšně změněn na ve zpracování!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async markReferenceAsDone(referenceRecord: Rec, searchParams: Partial<RecordSearchParams>): Promise<void> {
        try {
            await this.modalDialogService.openModalWindow('confirmationDialog', {text: 'Nastavit výkazy stavem "Ke schválení"?'});
            await this.markReportsWithState(referenceRecord, searchParams, SUTOR_REPORT_STATES.TO_CONFIRM);
            this.toastMessageService.showSuccess('Stav úšpěšně změněn na hotový!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    private async markReportsWithState(referenceRecord: Rec, searchParams: Partial<RecordSearchParams>, newState: typeof SUTOR_REPORT_STATES[keyof typeof SUTOR_REPORT_STATES]): Promise<void> {
        try {
            await this.recordBulkEditService.bulkEditRecords(addFilterQtToSearchParams(searchParams, {
                negatedFilteredValues: {
                    [mapFieldTypeIdToFilterField(FOND_REPORT.stateFieldId, 'val')]: {
                        datatype: FilterableDatatype.CODEBOOK_VALUE,
                        val: SUTOR_REPORT_STATES.CONFIRMED
                    }
                }
            }), {
                fieldEditRequests: [
                    {
                        id: convertFieldTypeIdToFieldId(FOND_REPORT.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: newState}
                    }
                ],
                additionalInvalidatingRecords: [referenceRecord.id]
            });
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async shareWithDepartment(record: Rec, targetDepartment: DepartmentResponse): Promise<RecordHolding | null> {
        try {
            const holding = await this.recordService.createRecordHolding(record, targetDepartment);
            this.toastMessageService.showSuccess(`Průvodka byla nasdílena s divizí ${pipe(targetDepartment, loc())}!`);
            return holding;
        } catch {
            return null;
        }
    }

    public async removeSharingWithDepartment(recordHolding: RecordHolding): Promise<boolean> {
        try {
            await this.recordService.deleteRecordHolding(recordHolding);
            this.toastMessageService.showSuccess(`Zrušeno sdílení průvodky s divizí ${pipe(recordHolding.department, loc())}!`);
            return true;
        } catch {
            return false;
        }
    }
}